import * as React from 'react';
import { Transition } from 'react-transition-group';
import cx from 'classnames';
import {
  getWindow,
  StatusIconMap,
  SvgCloseSmall,
  Box,
  useSafeContext,
  ButtonBase,
  useMediaQuery,
} from '../../utils/index.js';
import { IconButton } from '../Buttons/IconButton.js';
import { ToasterStateContext } from './Toaster.js';
export const Toast = (props) => {
  let {
    content,
    category,
    type = 'temporary',
    isVisible: isVisibleProp,
    link,
    duration = 7000,
    hasCloseButton,
    onRemove,
    animateOutTo,
    domProps,
  } = props;
  let closeTimeout = React.useRef(0);
  let { placement } = useSafeContext(ToasterStateContext).settings;
  let placementPosition = placement.startsWith('top') ? 'top' : 'bottom';
  let [visible, setVisible] = React.useState(isVisibleProp ?? true);
  let isVisible = isVisibleProp ?? visible;
  let [height, setHeight] = React.useState(0);
  let thisElement = React.useRef(null);
  let [margin, setMargin] = React.useState(0);
  let motionOk = useMediaQuery('(prefers-reduced-motion: no-preference)');
  let marginStyle = () => {
    if ('top' === placementPosition)
      return {
        marginBlockEnd: margin,
      };
    return {
      marginBlockStart: margin,
    };
  };
  React.useEffect(() => {
    if ('temporary' === type) setCloseTimeout(duration);
    return () => {
      clearCloseTimeout();
    };
  }, [duration, type]);
  React.useEffect(() => {
    if (!isVisible && !animateOutTo) setMargin(-height);
  }, [isVisible, animateOutTo, setMargin, height]);
  let close = () => {
    clearCloseTimeout();
    setMargin(-height);
    setVisible(false);
  };
  let setCloseTimeout = (timeout) => {
    let definedWindow = getWindow();
    if (!definedWindow) return;
    closeTimeout.current = definedWindow.setTimeout(() => {
      close();
    }, timeout);
  };
  let clearCloseTimeout = () => {
    getWindow()?.clearTimeout(closeTimeout.current);
  };
  let onRef = (ref) => {
    if (ref) {
      let { height } = ref.getBoundingClientRect();
      setHeight(height);
    }
  };
  let calculateOutAnimation = (node) => {
    let translateX = 0;
    let translateY = 0;
    if (animateOutTo && node) {
      let { x: startX, y: startY } = node.getBoundingClientRect();
      let { x: endX, y: endY } = animateOutTo.getBoundingClientRect();
      translateX = endX - startX;
      translateY = endY - startY;
    }
    return {
      translateX,
      translateY,
    };
  };
  return React.createElement(
    Transition,
    {
      timeout: {
        enter: 240,
        exit: animateOutTo ? 400 : 120,
      },
      in: isVisible,
      appear: true,
      unmountOnExit: true,
      onEnter: (node) => {
        if (motionOk) {
          node.style.transform = 'translateY(15%)';
          node.style.transitionTimingFunction = 'ease';
        }
      },
      onEntered: (node) => {
        if (motionOk) node.style.transform = 'translateY(0)';
      },
      onExiting: (node) => {
        if (motionOk) {
          let { translateX, translateY } = calculateOutAnimation(node);
          node.style.transform = animateOutTo
            ? `scale(0.9) translate(${translateX}px,${translateY}px)`
            : 'scale(0.9)';
          node.style.opacity = '0';
          node.style.transitionDuration = animateOutTo ? '400ms' : '120ms';
          node.style.transitionTimingFunction = 'cubic-bezier(0.4, 0, 1, 1)';
        }
      },
      onExited: onRemove,
    },
    React.createElement(
      Box,
      {
        ref: thisElement,
        className: 'iui-toast-all',
        style: {
          height,
          ...marginStyle(),
        },
      },
      React.createElement(
        'div',
        {
          ref: onRef,
        },
        React.createElement(ToastPresentation, {
          as: 'div',
          category: category,
          content: content,
          link: link,
          type: type,
          hasCloseButton: hasCloseButton,
          onClose: close,
          ...domProps?.toastProps,
          contentProps: domProps?.contentProps,
        }),
      ),
    ),
  );
};
export const ToastPresentation = React.forwardRef((props, forwardedRef) => {
  let {
    content,
    category,
    type = 'temporary',
    link,
    hasCloseButton,
    onClose,
    className,
    contentProps,
    ...rest
  } = props;
  let StatusIcon = StatusIconMap[category];
  return React.createElement(
    Box,
    {
      className: cx(`iui-toast iui-${category}`, className),
      ref: forwardedRef,
      ...rest,
    },
    React.createElement(
      Box,
      {
        className: 'iui-status-area',
      },
      React.createElement(StatusIcon, {
        className: 'iui-icon',
      }),
    ),
    React.createElement(
      Box,
      {
        as: 'div',
        ...contentProps,
        className: cx('iui-message', contentProps?.className),
      },
      content,
    ),
    link &&
      React.createElement(
        ButtonBase,
        {
          ...link,
          className: cx('iui-anchor', 'iui-toast-anchor', link.className),
          title: void 0,
          'data-iui-status': category,
          'data-iui-underline': true,
        },
        link.title,
      ),
    ('persisting' === type || hasCloseButton) &&
      React.createElement(
        IconButton,
        {
          size: 'small',
          styleType: 'borderless',
          onClick: onClose,
          'aria-label': 'Close',
        },
        React.createElement(SvgCloseSmall, null),
      ),
  );
});
